import { useState } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../lib/constants";

const FAQ_ITEMS = [
  {
    q: "How do I subscribe to a channel?",
    a: "Click the subscription link shared by the channel admin. Choose a plan, complete payment via Razorpay, and access is granted instantly.",
  },
  {
    q: "My access was revoked — what happened?",
    a: "Access is revoked when a subscription expires, payment fails, or the admin manually revokes it. Check your subscription status or contact the channel admin directly.",
  },
  {
    q: "I paid but didn't get access.",
    a: "Payment verification can take up to 2 minutes. If access is not granted after 5 minutes, contact us with your transaction ID and we will resolve it promptly.",
  },
  {
    q: "How do I cancel a subscription?",
    a: "Subscriptions are currently non-recurring — you pay per period. Simply don't renew when your plan expires. Contact the admin if you need an early refund.",
  },
  {
    q: "I'm an admin — how do I set up my workspace?",
    a: "After registering, go to Settings → Platform to add your bot token. The webhook is set automatically. Then create channels and generate referral links to share with subscribers.",
  },
  {
    q: "How do I add the bot to my Telegram channel?",
    a: "In Settings, add your bot token. A QR code and step-by-step instructions will be shown. Once you add the bot as an admin, it registers automatically.",
  },
  {
    q: "How do I update my payment credentials?",
    a: "Go to Settings → Payment Integration and add your Razorpay live keys. Test mode uses platform-level test credentials automatically.",
  },
  {
    q: "What is KYC and do I need it?",
    a: "KYC (Know Your Customer) is an optional identity verification step. Admins can enable it per their compliance requirements. If KYC is disabled for your admin's workspace, it won't appear in your flow.",
  },
];

export default function SupportPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production this would POST to a support endpoint
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-dark-primary text-text-primary font-inter">
      {/* Nav */}
      <nav className="border-b border-dark-border/30 bg-dark-primary/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={ROUTES.LANDING} className="text-xl font-black font-outfit tracking-tighter text-golden-accent">
            SuBs
          </Link>
          <Link to={ROUTES.LANDING} className="text-sm text-text-secondary hover:text-golden-accent transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-golden-accent mb-3">Help Center</p>
          <h1 className="text-4xl md:text-5xl font-black font-outfit tracking-tighter text-text-primary mb-4">
            Support
          </h1>
          <p className="text-text-secondary max-w-xl mx-auto">
            Find answers below or send us a message — we'll get back to you as soon as possible.
          </p>
        </div>

        {/* FAQ */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold font-outfit text-text-primary mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div
                key={i}
                className="rounded-xl border border-dark-border/50 bg-dark-secondary/50 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-dark-tertiary/40 transition-colors"
                >
                  <span className="font-medium text-text-primary">{item.q}</span>
                  <span className={`text-golden-accent text-xl leading-none transition-transform duration-200 flex-shrink-0 ${openIndex === i ? "rotate-45" : ""}`}>
                    +
                  </span>
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-5 text-text-secondary text-sm leading-relaxed border-t border-dark-border/30 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div className="rounded-2xl border border-dark-border/50 bg-dark-secondary/50 p-8">
          <h2 className="text-2xl font-bold font-outfit text-text-primary mb-2">Still need help?</h2>
          <p className="text-text-secondary mb-8 text-sm">Send us a message and we'll respond within 1–2 business days.</p>

          {submitted ? (
            <div className="rounded-xl border border-functional-success/30 bg-functional-success/10 p-6 text-center">
              <p className="text-functional-success font-semibold text-lg mb-1">Message sent!</p>
              <p className="text-text-secondary text-sm">We'll get back to you at <span className="text-text-primary">{email}</span> soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="s-name" className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">
                    Name
                  </label>
                  <input
                    id="s-name"
                    type="text"
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-lg border border-dark-border/60 bg-dark-primary px-4 py-3 text-text-primary placeholder-text-secondary/50 text-sm focus:outline-none focus:border-golden-accent/60 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="s-email" className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">
                    Email
                  </label>
                  <input
                    id="s-email"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-dark-border/60 bg-dark-primary px-4 py-3 text-text-primary placeholder-text-secondary/50 text-sm focus:outline-none focus:border-golden-accent/60 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="s-message" className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="s-message"
                  required
                  rows={5}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe your issue in detail..."
                  className="w-full rounded-lg border border-dark-border/60 bg-dark-primary px-4 py-3 text-text-primary placeholder-text-secondary/50 text-sm focus:outline-none focus:border-golden-accent/60 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-golden-accent text-dark-primary font-bold rounded-full text-sm hover:scale-105 active:scale-95 transition-all uppercase tracking-wide"
              >
                Send Message
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-dark-border/30 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-text-secondary">
          <p>© {new Date().getFullYear()} SuBs. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to={ROUTES.TERMS} className="hover:text-golden-accent transition-colors">Terms</Link>
            <Link to={ROUTES.PRIVACY} className="hover:text-golden-accent transition-colors">Privacy</Link>
            <Link to={ROUTES.SUPPORT} className="hover:text-golden-accent transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
